import { FC, ReactElement, useState } from 'react';
import * as React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'dibs-react-intl';
import { createRefetchContainer, graphql, RelayRefetchProp } from 'react-relay/legacy';
import { PayloadError } from 'relay-runtime';
import serverVars from 'server-vars';

import { CreatableSearchSelect } from 'dibs-elements/exports/CreatableSearchSelect';
import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

// this creates project but can't re-use "CreateProjectMutation" which already exists in dibs-project-forms
import addProjectMutation from '../../mutations/addProjectMutation';
import styles from './SelectProject.scss';
import { SharedSelectProject_viewer$data } from './__generated__/SharedSelectProject_viewer.graphql';
import { SharedSelectProject_user$data } from './__generated__/SharedSelectProject_user.graphql';

type Projects = NonNullable<SharedSelectProject_viewer$data['projects']>['edges'];
export type SelectedProjectType = NonNullable<ArrayElement<Projects>>['node'] & {
    isNewProject?: boolean;
};

type RelayError = {
    requestInfo: {
        status: number | null;
    } | null;
};

type RelayErrors = {
    source?: {
        errors?: RelayError[];
    } | null;
};

type Props = {
    label?: React.ReactElement;
    onCreateProjectClick?: () => void;
    onProjectSelection: (project: SelectedProjectType) => void;
    relay: RelayRefetchProp;
    selectedProject: SelectedProjectType | null;
    user: SharedSelectProject_user$data;
    viewer: SharedSelectProject_viewer$data;
    clearable?: boolean;
    selectSize?: string;
};

const messages = defineMessages({
    projectSelectPlaceholder: {
        id: 'dbl.contactModal.SharedSelectProject.select_placeholder',
        defaultMessage: 'Select or Create Project',
    },
});

export const SharedSelectProject: FC<Props> = ({
    label,
    selectSize,
    clearable,
    selectedProject,
    onProjectSelection,
    onCreateProjectClick,
    relay,
    user,
    viewer,
}) => {
    const [errorMessage, setErrorMessage] = useState<ReactElement | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const intl = useIntl();

    const handleProjectCreationError = (
        error: Error | ReadonlyArray<PayloadError> | null | undefined
    ): void => {
        const err = error as RelayErrors;
        const errors = Array.isArray(err) ? err : err?.source?.errors || [];

        const isProjectNameCollision = !!errors.find(e => e?.requestInfo?.status === 409);

        setErrorMessage(
            isProjectNameCollision ? (
                <FormattedMessage
                    id="dbl.contactModal.SharedSelectProject.project_name_collision"
                    defaultMessage={`You already have a project named "{newProjectName}"`}
                    values={{ newProjectName }}
                />
            ) : (
                <FormattedMessage
                    id="dbl.contactModal.SharedSelectProject.project_create_error"
                    defaultMessage="Error: could not create project"
                />
            )
        );
        setIsLoading(false);

        // eslint-disable-next-line no-console
        console.error('Project creation failed', error);
    };

    const projects = (viewer.projects?.edges || []).map(edge => edge?.node);
    const projectOptions = projects.map(project => ({
        value: project?.serviceId || '',
        label: project?.name || '',
    }));

    const size = selectSize || (serverVars.get('settings.isMobile') ? 'medium' : 'large');

    return (
        <div className={styles.selectProjectWrapper}>
            <CreatableSearchSelect
                clearable={clearable}
                createOptionHandler={({ inputValue }) => {
                    if (inputValue) {
                        if (onCreateProjectClick) {
                            onCreateProjectClick();
                        }

                        setIsLoading(true);
                        setNewProjectName(inputValue);

                        addProjectMutation(relay.environment, {
                            input: {
                                name: inputValue,
                                userId: user.serviceId || '',
                            },
                            onCompleted: (response, errors): void => {
                                if (errors && errors.length) {
                                    handleProjectCreationError(errors);
                                    return;
                                }

                                const newProject = response?.createProject?.project;
                                const refetchVars = { userIds: [user.serviceId], hasUserId: true };

                                if (!newProject) {
                                    return;
                                }

                                relay.refetch(
                                    refetchVars,
                                    null,
                                    () => {
                                        onProjectSelection({
                                            ...newProject,
                                            isNewProject: true,
                                        });
                                        setErrorMessage(null);
                                        setIsLoading(false);
                                        setNewProjectName('');
                                    },
                                    { force: true }
                                );
                            },
                            onError: handleProjectCreationError,
                        });
                    }
                }}
                dataTn="select-project-dbl"
                errorMessage={errorMessage}
                isLoading={isLoading}
                label={label}
                name="project"
                onChange={selectedProjectOption => {
                    let selected = null;
                    if (selectedProjectOption) {
                        selected = projects.find(p => p?.serviceId === selectedProjectOption.value);
                    }
                    setErrorMessage(null);

                    if (selected) {
                        onProjectSelection(selected);
                    }
                }}
                options={projectOptions}
                placeholder={intl.formatMessage(messages.projectSelectPlaceholder)}
                promptGenerator={inputValue => (
                    <FormattedMessage
                        id="dbl.contactModal.SharedSelectProject.create_project_cta"
                        defaultMessage="Create New Project: {inputValue}"
                        values={{ inputValue }}
                    />
                )}
                size={size}
                value={selectedProject?.serviceId || undefined}
            />
        </div>
    );
};

export default createRefetchContainer(
    SharedSelectProject,
    {
        viewer: graphql`
            fragment SharedSelectProject_viewer on Viewer {
                projects(userIds: $userIds, first: 200) @include(if: $hasUserId) {
                    edges {
                        node {
                            serviceId
                            name
                            portfolio {
                                serviceId
                            }
                        }
                    }
                }
            }
        `,
        user: graphql`
            fragment SharedSelectProject_user on User {
                serviceId
            }
        `,
    },
    graphql`
        query SharedSelectProjectRefetchQuery($userIds: [String], $hasUserId: Boolean = true) {
            viewer {
                ...SharedSelectProject_viewer
            }
        }
    `
);
